export const storage = {
  title: 'Object Storage',
  subtitle: 'S3-compatible object storage',

  buckets: {
    title: 'Buckets',
    empty: 'No buckets yet',
    emptyHint: 'Create your first bucket to start storing objects.',
    columns: {
      name: 'Name',
      region: 'Region',
      storage: 'Storage',
      objects: 'Objects',
      cost: 'Cost/Mo.',
      created: 'Created',
      actions: 'Actions'
    },
    endpoint: 'Endpoint',
    endpointValue: 'https://storage.itsh.dev',
    create: {
      title: 'Create Bucket',
      subtitle: 'Add a new S3-compatible storage bucket',
      name: 'Bucket Name',
      namePlaceholder: 'my-bucket',
      nameHint: '3-63 characters, lowercase letters, numbers, and hyphens',
      region: 'Region',
      button: 'Create Bucket',
      invalidName: 'Invalid bucket name',
      checking: 'Checking name ...',
      available: 'Name is available',
      takenHint:
        'Bucket names are shared across all customers, so this one has to be globally unique. Prefixing it with your project or company name usually helps.'
    },
    delete: {
      title: 'Delete Bucket',
      message: 'Are you sure you want to delete this bucket? All stored objects will be permanently removed.',
      warning:
        'This action cannot be undone. The name is released immediately and any other customer can claim it.',
      force: 'Force delete (remove all objects)',
      button: 'Delete Bucket',
      failed: 'The bucket could not be deleted.',
      deactivatedCredentials:
        'These credentials were scoped only to this bucket and have been deactivated:'
    },
    public: {
      label: 'Public access',
      enable: 'Make public',
      disable: 'Make private',
      enabled: 'Public',
      disabled: 'Private',
      url: 'Public URL',
      hint: 'Anyone can GET objects. Uploads still require credentials.',
      hintPrivate: 'Only requests with valid credentials can read or upload objects.',
      warning: 'All current and future objects in this bucket will be publicly accessible.',
      confirmEnable: 'Are you sure you want to enable public access? All objects in this bucket — existing and future — will be accessible to anyone on the internet.',
      confirm: 'Make public'
    },
    regions: {
      'eu-central-1': 'EU Central (Nuremberg)',
      'eu-central-2': 'EU Central (Falkenstein)',
      'eu-north-1': 'EU North (Helsinki)'
    }
  },

  credentials: {
    title: 'Access Keys',
    empty: 'No access keys yet',
    emptyHint: 'Create an access key to connect to your storage via S3-compatible tools.',
    columns: {
      name: 'Name',
      accessKey: 'Access Key',
      status: 'Status',
      created: 'Created',
      lastUsed: 'Last Used',
      actions: 'Actions'
    },
    active: 'Active',
    inactive: 'Inactive',
    never: 'Never',
    create: {
      title: 'Create Access Key',
      subtitle: 'Generate a new S3-compatible access key pair',
      name: 'Key Name',
      namePlaceholder: 'my-app-key',
      button: 'Create Key'
    },
    rotate: {
      title: 'Rotate Secret',
      message: 'This will generate a new secret key. The old secret will be immediately invalidated.',
      button: 'Rotate Secret'
    },
    delete: {
      title: 'Delete Access Key',
      message: 'Are you sure you want to delete this access key? Applications using this key will lose access immediately.',
      button: 'Delete Key'
    },
    secret: {
      title: 'Access Key Created',
      warning: 'Save these credentials now. The secret key will not be shown again.',
      accessKey: 'Access Key ID',
      secretKey: 'Secret Access Key',
      endpoint: 'S3 Endpoint',
      copied: 'Copied!',
      tabs: {
        awsCli: 'AWS CLI',
        rclone: 'rclone',
        s3cmd: 's3cmd'
      }
    },
    scope: {
      label: 'Bucket access',
      all: 'All buckets',
      restricted: 'Restricted',
      help: 'Leave empty for access to all buckets',
      edit: 'Edit scope'
    },
    levels: {
      selectLabel: 'Access level for {bucket}',
      defaultLabel: 'Level for all buckets',
      defaultHelp: 'Applies to every bucket in your account while no individual buckets are selected.',
      defaultIgnored:
        'Not used while individual buckets are selected: each bucket then uses its own level.',
      publicWarning:
        'Public buckets ({buckets}) can be read by anyone without a key. Write-only and read-only do not change that.',
      ro: {
        label: 'Read only',
        short: 'Read'
      },
      wo: {
        label: 'Write only (no reading, no deleting)',
        short: 'Write'
      },
      rw: {
        label: 'Read and write (including delete)',
        short: 'Read/write'
      },
      full: {
        label: 'Full (including bucket settings)',
        short: 'Full'
      }
    }
  },

  notifications: {
    bucketCreated: 'Bucket created',
    bucketDeleted: 'Bucket deleted',
    credentialCreated: 'Access key created',
    credentialDeleted: 'Access key deleted',
    credentialRotated: 'Secret key rotated',
    scopeUpdated: 'Bucket scope updated',
    bucketPublic: 'Bucket is now public',
    bucketPrivate: 'Bucket is now private'
  }
} as const
